import { useEffect, useState } from 'react';
import { api } from '../services/api';

interface Alert {
  id: number;
  timestamp: string;
  severity: string;
  title: string;
  message: string;
  metric_name: string | null;
  metric_value: number | null;
  threshold_value: number | null;
  acknowledged: boolean;
  acknowledged_at: string | null;
  resolved: boolean;
  resolved_at: string | null;
}

interface AlertStats {
  period_hours: number;
  by_severity: {
    critical: number;
    warning: number;
    info: number;
  };
  by_status: {
    total: number;
    unacknowledged: number;
    unresolved: number;
    in_progress: number;
  };
  by_type: Array<{ title: string; count: number }>;
}

interface AlertManagementProps {
  refreshInterval?: number;
}

export function AlertManagement({ refreshInterval = 30000 }: AlertManagementProps) {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [stats, setStats] = useState<AlertStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAlerts, setSelectedAlerts] = useState<Set<number>>(new Set());
  const [processing, setProcessing] = useState(false);

  // Filters
  const [filter, setFilter] = useState({
    severity: '',
    acknowledged: '',
    resolved: '',
    hours: 24
  });

  // View state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedAlert, setExpandedAlert] = useState<number | null>(null);

  // New alert form
  const [newAlert, setNewAlert] = useState({
    title: '',
    message: '',
    severity: 'WARNING'
  });

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval, filter]);

  const loadData = async () => {
    try {
      const [alertsData, statsData] = await Promise.all([
        api.getAlerts({
          severity: filter.severity || undefined,
          acknowledged: filter.acknowledged ? filter.acknowledged === 'true' : undefined,
          resolved: filter.resolved ? filter.resolved === 'true' : undefined,
          hours: filter.hours
        }),
        api.getAlertStats(filter.hours)
      ]);
      setAlerts(alertsData.alerts);
      setStats(statsData);
      setLoading(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load alerts');
      setLoading(false);
    }
  };

  const acknowledgeAlert = async (alertId: number) => {
    try {
      setProcessing(true);
      await api.acknowledgeAlert(alertId);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to acknowledge alert');
    } finally {
      setProcessing(false);
    }
  };

  const resolveAlert = async (alertId: number) => {
    try {
      setProcessing(true);
      await api.resolveAlert(alertId);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resolve alert');
    } finally {
      setProcessing(false);
    }
  };

  const acknowledgeSelected = async () => {
    if (selectedAlerts.size === 0) return;
    try {
      setProcessing(true);
      await api.acknowledgeAlerts(Array.from(selectedAlerts));
      setSelectedAlerts(new Set());
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to acknowledge alerts');
    } finally {
      setProcessing(false);
    }
  };

  const resolveSelected = async () => {
    if (selectedAlerts.size === 0) return;
    try {
      setProcessing(true);
      await api.resolveAlerts(Array.from(selectedAlerts));
      setSelectedAlerts(new Set());
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resolve alerts');
    } finally {
      setProcessing(false);
    }
  };

  const createAlert = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setProcessing(true);
      await api.createAlert(newAlert);
      setNewAlert({ title: '', message: '', severity: 'WARNING' });
      setShowCreateForm(false);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create alert');
    } finally {
      setProcessing(false);
    }
  };

  const toggleSelectAlert = (alertId: number) => {
    const newSelected = new Set(selectedAlerts);
    if (newSelected.has(alertId)) {
      newSelected.delete(alertId);
    } else {
      newSelected.add(alertId);
    }
    setSelectedAlerts(newSelected);
  };

  const selectAllUnresolved = () => {
    const unresolvedIds = alerts.filter(a => !a.resolved).map(a => a.id);
    setSelectedAlerts(new Set(unresolvedIds));
  };

  const clearSelection = () => {
    setSelectedAlerts(new Set());
  };

  const getSeverityClass = (severity: string) => {
    switch (severity.toUpperCase()) {
      case 'CRITICAL': return 'severity-critical';
      case 'WARNING': return 'severity-warning';
      case 'INFO': return 'severity-info';
      default: return '';
    }
  };

  const formatTime = (iso: string) => {
    const date = new Date(iso);
    return date.toLocaleString();
  };

  const formatRelativeTime = (iso: string) => {
    const date = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    if (diffMins > 0) return `${diffMins}m ago`;
    return 'just now';
  };

  if (loading && alerts.length === 0) {
    return <div className="alert-management loading">Loading alerts...</div>;
  }

  return (
    <div className="alert-management">
      <div className="alerts-header">
        <h3>Alert Management</h3>
        <div className="header-controls">
          <button onClick={() => setShowCreateForm(!showCreateForm)} className="new-alert-btn">
            {showCreateForm ? 'Cancel' : '+ Test Alert'}
          </button>
          <button onClick={loadData} className="refresh-btn" title="Refresh">↻</button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Stats Summary */}
      {stats && (
        <div className="alert-stats">
          <div className={`stat-card ${stats.by_severity.critical > 0 ? 'has-critical' : ''}`}>
            <span className="stat-value critical">{stats.by_severity.critical}</span>
            <span className="stat-label">Critical</span>
          </div>
          <div className={`stat-card ${stats.by_severity.warning > 0 ? 'has-warning' : ''}`}>
            <span className="stat-value warning">{stats.by_severity.warning}</span>
            <span className="stat-label">Warning</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{stats.by_status.unacknowledged}</span>
            <span className="stat-label">Unacknowledged</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{stats.by_status.unresolved}</span>
            <span className="stat-label">Unresolved</span>
          </div>
        </div>
      )}

      {/* Create Alert Form */}
      {showCreateForm && (
        <form className="create-alert-form" onSubmit={createAlert}>
          <div className="form-row">
            <label>Severity</label>
            <select
              value={newAlert.severity}
              onChange={(e) => setNewAlert({ ...newAlert, severity: e.target.value })}
            >
              <option value="INFO">Info</option>
              <option value="WARNING">Warning</option>
              <option value="CRITICAL">Critical</option>
            </select>
          </div>
          <div className="form-row">
            <label>Title</label>
            <input
              type="text"
              placeholder="Alert title"
              value={newAlert.title}
              onChange={(e) => setNewAlert({ ...newAlert, title: e.target.value })}
              required
            />
          </div>
          <div className="form-row full-width">
            <label>Message</label>
            <textarea
              placeholder="Alert message"
              value={newAlert.message}
              onChange={(e) => setNewAlert({ ...newAlert, message: e.target.value })}
              required
            />
          </div>
          <button type="submit" className="submit-btn" disabled={processing}>
            {processing ? 'Creating...' : 'Create Alert'}
          </button>
        </form>
      )}

      {/* Filters */}
      <div className="alert-filters">
        <select
          value={filter.severity}
          onChange={(e) => setFilter({ ...filter, severity: e.target.value })}
        >
          <option value="">All Severities</option>
          <option value="CRITICAL">Critical</option>
          <option value="WARNING">Warning</option>
          <option value="INFO">Info</option>
        </select>
        <select
          value={filter.acknowledged}
          onChange={(e) => setFilter({ ...filter, acknowledged: e.target.value })}
        >
          <option value="">All</option>
          <option value="false">Unacknowledged</option>
          <option value="true">Acknowledged</option>
        </select>
        <select
          value={filter.resolved}
          onChange={(e) => setFilter({ ...filter, resolved: e.target.value })}
        >
          <option value="">All</option>
          <option value="false">Unresolved</option>
          <option value="true">Resolved</option>
        </select>
        <select
          value={filter.hours}
          onChange={(e) => setFilter({ ...filter, hours: parseInt(e.target.value) })}
        >
          <option value={1}>Last hour</option>
          <option value={6}>Last 6 hours</option>
          <option value={24}>Last 24 hours</option>
          <option value={168}>Last 7 days</option>
          <option value={720}>Last 30 days</option>
        </select>
      </div>

      {/* Bulk Actions */}
      {selectedAlerts.size > 0 && (
        <div className="bulk-actions">
          <span>{selectedAlerts.size} selected</span>
          <button onClick={acknowledgeSelected} disabled={processing}>Acknowledge</button>
          <button onClick={resolveSelected} disabled={processing}>Resolve</button>
          <button onClick={clearSelection}>Clear</button>
        </div>
      )}

      {/* Alert List */}
      <div className="alert-list">
        {alerts.length === 0 ? (
          <div className="no-alerts">No alerts found for the selected filters</div>
        ) : (
          <>
            <div className="list-header">
              <button className="select-all-btn" onClick={selectAllUnresolved}>
                Select Unresolved
              </button>
              <span className="alert-count">{alerts.length} alerts</span>
            </div>
            {alerts.map((alert) => (
              <div
                key={alert.id}
                className={`alert-item ${getSeverityClass(alert.severity)} ${alert.resolved ? 'resolved' : ''} ${selectedAlerts.has(alert.id) ? 'selected' : ''}`}
              >
                <div className="alert-header" onClick={() => setExpandedAlert(expandedAlert === alert.id ? null : alert.id)}>
                  <input
                    type="checkbox"
                    checked={selectedAlerts.has(alert.id)}
                    onChange={() => toggleSelectAlert(alert.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className={`severity-badge ${getSeverityClass(alert.severity)}`}>
                    {alert.severity}
                  </span>
                  <span className="alert-title">{alert.title}</span>
                  <span className="alert-time" title={formatTime(alert.timestamp)}>
                    {formatRelativeTime(alert.timestamp)}
                  </span>
                  <div className="alert-status">
                    {alert.acknowledged && <span className="status-badge ack">ACK</span>}
                    {alert.resolved && <span className="status-badge resolved">RESOLVED</span>}
                  </div>
                </div>

                {expandedAlert === alert.id && (
                  <div className="alert-details">
                    <p className="alert-message">{alert.message}</p>
                    {alert.metric_name && (
                      <div className="alert-metrics">
                        <span><strong>Metric:</strong> {alert.metric_name}</span>
                        {alert.metric_value !== null && (
                          <span><strong>Value:</strong> {alert.metric_value}</span>
                        )}
                        {alert.threshold_value !== null && (
                          <span><strong>Threshold:</strong> {alert.threshold_value}</span>
                        )}
                      </div>
                    )}
                    <div className="alert-timestamps">
                      <span>Created: {formatTime(alert.timestamp)}</span>
                      {alert.acknowledged_at && <span>Acknowledged: {formatTime(alert.acknowledged_at)}</span>}
                      {alert.resolved_at && <span>Resolved: {formatTime(alert.resolved_at)}</span>}
                    </div>
                    <div className="alert-actions">
                      {!alert.acknowledged && (
                        <button
                          onClick={() => acknowledgeAlert(alert.id)}
                          disabled={processing}
                          className="ack-btn"
                        >
                          Acknowledge
                        </button>
                      )}
                      {!alert.resolved && (
                        <button
                          onClick={() => resolveAlert(alert.id)}
                          disabled={processing}
                          className="resolve-btn"
                        >
                          Resolve
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>

      {/* Alert Types Summary */}
      {stats && stats.by_type.length > 0 && (
        <div className="alert-types">
          <h4>Alert Types (Last {filter.hours}h)</h4>
          <div className="type-list">
            {stats.by_type.map((item, index) => (
              <div key={index} className="type-item">
                <span className="type-name">{item.title}</span>
                <span className="type-count">{item.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
